import { getSafeSupabaseClient } from '../supabaseSync';
import { unwrapRpcResult } from './rpcEnvelope';

export type RevisionResolution = 'CLOSED' | 'WITHDRAWN' | 'SUPERSEDED';

export interface CloseRevisionRequestArgs {
  revisionRequestId: string;
  resolution: RevisionResolution;
  actorId: string;
  actorName?: string | null;
  note?: string | null;
}

export interface CloseRevisionRequestResult {
  success: boolean;
  revision_request_id: string;
  loop_id: string;
  loop_state: string;
}

/**
 * Close a revision request.
 * 
 * Atomic transaction:
 * - Updates revision_requests status
 * - If no more open revision requests, returns loop to 'open' state
 * - Records loop transition (if state changed)
 * - Emits governance event (revision_request_closed)
 */
export async function closeRevisionRequest(args: CloseRevisionRequestArgs): Promise<CloseRevisionRequestResult> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    throw new Error('Supabase client not available');
  }

  const validResolutions: RevisionResolution[] = ['CLOSED', 'WITHDRAWN', 'SUPERSEDED'];
  if (!validResolutions.includes(args.resolution)) {
    throw new Error(`Invalid resolution: ${args.resolution}. Expected: ${validResolutions.join('|')}`);
  }

  const { data, error } = await supabase.rpc('catchball_close_revision_request', {
    p_revision_request_id: args.revisionRequestId,
    p_resolution: args.resolution,
    p_actor_id: args.actorId,
    p_actor_name: args.actorName ?? null,
    p_note: args.note ?? null,
  });

  // Refusals arrive either as a PostgREST error or as { success: false, error }.
  return unwrapRpcResult<CloseRevisionRequestResult>(data, error, 'The server refused closing the revision request.');
}
